import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AssetOverviewService } from '../../services/asset-overview.service';
import {
  AssetFuelSection,
  AssetLifecycleSection,
  AssetOverviewSummaryResponse,
  AssetRegistrationDetailsResponse,
  BasicVehicleDetailsResponse,
  MechanicalPerformanceDataResponse,
  VehicleDetailsResponse,
  VehicleSpecificationsResponse,
} from '../../models/asset-details-response.model';
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { Subject } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EditLifecyclePointsModalComponent } from './modals/edit-lifecycle-points-modal/edit-lifecycle-points-modal.component';
import { ASSET_IMAGE_ACCEPTED_TYPES, ASSET_IMAGE_MAX_SIZE_BYTES, ASSET_IMAGE_MAX_SIZE_MB } from '../../constants/asset-image.constants';
import { EMPTY_REGISTRATION, MIN_LIFECYCLE_VALUE_PERCENT } from '../../constants/asset-information.constants';
import { POWER_UNIT, TRACKED_VEHICLE_FIELDS, WEIGHT_UNIT } from '../../constants/vehicle-information.constants';
import { hasValue } from '../../../../_shared/helpers/value.helper';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-asset-information',
  standalone: true,
  imports: [CommonModule, TranslocoDirective, TranslocoPipe],
  templateUrl: './asset-information.component.html',
  styleUrl: './asset-information.component.scss',
})
export class AssetInformationComponent implements OnInit, OnDestroy {
  private assetOverviewService = inject(AssetOverviewService);
  private errorHandlerService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);
  private modalService = inject(NgbModal);
  private readonly transloco: TranslocoService = inject(TranslocoService);

  @Input() assetId: number;
  @Input() assetName: string;

  @Output() assetNameChange = new EventEmitter<string>();

  readonly acceptedImageTypes = ASSET_IMAGE_ACCEPTED_TYPES.join(',');
  readonly maxImageSizeMb = ASSET_IMAGE_MAX_SIZE_MB;

  isLoading: boolean = true;
  isLoadingVehicleDetails: boolean = true;
  isLoadingAssetImage: boolean = true;
  isRefreshingOverview: boolean = false;
  isUploadingImage: boolean = false;
  isImageDragOver: boolean = false;
  isImageLoaded: boolean = false;
  assetImageUrl: string | null = null;
  hasVehicleDetails: boolean = false;
  hasPartialVehicleDetails: boolean = false;
  isPartialNoticeDismissed: boolean = false;
  hasVehicleDetailsError: boolean = false;
  registration: AssetRegistrationDetailsResponse = EMPTY_REGISTRATION;
  fuel: AssetFuelSection | null = null;
  lifecycle: AssetLifecycleSection | null = null;
  vehicleSpecifications: VehicleSpecificationsResponse;
  basicVehicleDetails: BasicVehicleDetailsResponse;
  mechanicalPerformanceData: MechanicalPerformanceDataResponse;
  lifecycleFillPercent = 0;
  sellingPointPercent = 0;

  private readonly cancelSubject = new Subject<void>();

  ngOnInit(): void {
    this.getAssetInformation();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  getAssetInformation(): void {
    this.cancelSubject.next();

    // Deliberately not awaited - these all run in parallel.
    void this.getVehicleInformation();
    void this.getAssetImage();
    void this.getAssetOverview();
  }

  async getVehicleInformation(): Promise<void> {
    this.isLoadingVehicleDetails = true;
    this.hasVehicleDetailsError = false;

    try {
      const response = await this.assetOverviewService.getVehicleInformation(this.assetId, this.cancelSubject);
      this.setVehicleDetails(response?.data ?? null);

      this.isLoadingVehicleDetails = false;
    } catch (error: any) {
      // The handler skips cancelled requests, so this callback never runs on teardown.
      this.errorHandlerService.handleError(error, {
        showToast: false,
        callback: () => {
          this.hasVehicleDetails = false;
          this.hasPartialVehicleDetails = false;
          this.hasVehicleDetailsError = true;
          this.isLoadingVehicleDetails = false;
        }
      });
    }
  }

  async getAssetOverview(isRefresh: boolean = false): Promise<void> {
    this.isLoading = !isRefresh;
    this.isRefreshingOverview = isRefresh;

    try {
      const response = await this.assetOverviewService.getAssetOverviewSummary(this.assetId, this.cancelSubject);

      this.setOverviewSummary(response?.data ?? null);
      this.stopOverviewLoading();
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.setOverviewSummary(null);
          this.stopOverviewLoading();
        },
      });
    }
  }

  openEditLifecyclePoints(): void {
    const modalRef = this.modalService.open(EditLifecyclePointsModalComponent, { centered: true, backdrop: 'static' });
    modalRef.componentInstance.assetId = this.assetId;
    modalRef.componentInstance.assetName = this.registration.assetName || this.assetName;
    modalRef.componentInstance.licensePlate = this.registration.plateNumber;
    modalRef.componentInstance.mainColour = this.registration.mainColour;
    modalRef.componentInstance.mainColourHex = this.registration.mainColourHex;
    modalRef.componentInstance.sellingPointKm = this.lifecycle?.sellingPointKm ?? null;
    modalRef.componentInstance.retirePointKm = this.lifecycle?.retirePointKm ?? null;

    modalRef.closed.subscribe((updated: boolean) => {
      if (updated) {
        void this.getAssetOverview(true);
      }
    });
  }

  private setOverviewSummary(summary: AssetOverviewSummaryResponse | null): void {
    const renamedTo = summary?.registration?.assetName;
    if (renamedTo && renamedTo !== this.assetName) {
      this.assetName = renamedTo;
      this.assetNameChange.emit(renamedTo);
    }

    this.registration = summary?.registration ?? EMPTY_REGISTRATION;
    this.fuel = summary?.fuel ?? null;
    this.lifecycle = summary?.lifecycle ?? null;

    this.updateLifecycleDetails();
  }

  private stopOverviewLoading(): void {
    this.isLoading = false;
    this.isRefreshingOverview = false;
  }

  private async getAssetImage(): Promise<void> {
    this.isLoadingAssetImage = true;

    try {
      const response = await this.assetOverviewService.getAssetImage(this.assetId, this.cancelSubject);
      this.showAssetImage(response?.data?.url ?? null);

      this.isLoadingAssetImage = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: false,
        callback: () => {
          this.isLoadingAssetImage = false;
        }
      });
    }
  }

  dismissPartialNotice(): void {
    this.isPartialNoticeDismissed = true;
  }

  get showImageUpload(): boolean {
    return !this.isLoadingAssetImage && !this.assetImageUrl;
  }

  onImageDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isImageDragOver = true;
  }

  onImageDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isImageDragOver = false;
  }

  onImageDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isImageDragOver = false;

    const file = event.dataTransfer?.files?.[0];
    if (file) {
      void this.uploadAssetImage(file);
    }
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      void this.uploadAssetImage(file);
    }

    input.value = '';
  }

  onImageLoad(): void {
    this.isImageLoaded = true;
  }

  onImageError(): void {
    this.showAssetImage(null);
  }

  private async uploadAssetImage(file: File): Promise<void> {
    if (this.isUploadingImage || !this.isSupportedImage(file)) {
      return;
    }

    const previousImageUrl = this.assetImageUrl;
    this.isUploadingImage = true;

    const preview = await this.readImageAsDataUrl(file);
    if (preview) {
      this.showAssetImage(preview);
    }

    try {
      const response = await this.assetOverviewService.uploadAssetImage(this.assetId, file, this.cancelSubject);

      if (!preview) {
        this.showAssetImage(response?.data?.url ?? null);
      }

      this.isUploadingImage = false;
      this.toastr.success(this.transloco.translate('assetOverview.assetInformation.toasts.imageUploaded'));
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.showAssetImage(previousImageUrl);
          this.isUploadingImage = false;
        }
      });
    }
  }

  private isSupportedImage(file: File): boolean {
    if (!ASSET_IMAGE_ACCEPTED_TYPES.includes(file.type)) {
      this.toastr.error(this.transloco.translate('assetOverview.assetInformation.toasts.unsupportedImage'));
      return false;
    }

    if (file.size > ASSET_IMAGE_MAX_SIZE_BYTES) {
      this.toastr.error(this.transloco.translate('assetOverview.assetInformation.toasts.imageTooLarge', { size: ASSET_IMAGE_MAX_SIZE_MB }));
      return false;
    }

    return true;
  }

  private readImageAsDataUrl(file: File): Promise<string | null> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  }

  private showAssetImage(url: string | null): void {
    this.assetImageUrl = url;
    this.isImageLoaded = false;
  }

  private setVehicleDetails(details: VehicleDetailsResponse | null): void {
    const populatedFields = details ? TRACKED_VEHICLE_FIELDS.filter((field) => hasValue(details[field])).length : 0;

    this.hasVehicleDetails = populatedFields > 0;
    this.hasPartialVehicleDetails = this.hasVehicleDetails && populatedFields < TRACKED_VEHICLE_FIELDS.length;
    this.isPartialNoticeDismissed = false;

    if (!details || populatedFields === 0) {
      return;
    }

    this.vehicleSpecifications = {
      vin: details.vin,
      productionSequence: details.productionSequenceNumber,
      dateOfManufacture: details.dateOfManufacture,
      modelYear: details.modelYear?.toString() ?? null,
      countryOfManufacture: details.countryOfManufacture
    };

    this.basicVehicleDetails = {
      make: details.make,
      model: details.model,
      bodyType: details.bodyStyle,
      axleConfiguration: details.axleConfiguration
    };

    this.mechanicalPerformanceData = {
      engine: details.engine,
      power: this.formatMeasurement(details.powerHp, POWER_UNIT),
      transmission: details.transmission,
      grossVehicleWeight: this.formatMeasurement(details.grossVehicleWeight, WEIGHT_UNIT),
      fuelType: details.fuelType,
      co2Output: details.co2Output
    };
  }

  private formatMeasurement(value: number | null, unit: string): string | null {
    return value != null ? `${value.toLocaleString()} ${unit}` : null;
  }

  get overviewTitle(): string {
    return this.transloco.translate('assetOverview.assetInformation.overviewTitle', { name: this.registration.assetName || this.assetName });
  }

  get isLifecycleValueCentred(): boolean {
    return this.lifecycleFillPercent === 0;
  }

  get lifecycleValuePercent(): number {
    return this.isLifecycleValueCentred ? 50 : Math.max(this.lifecycleFillPercent, MIN_LIFECYCLE_VALUE_PERCENT);
  }

  get showSellingLabel(): boolean {
    const margin = 10;
    return (this.sellingPointPercent > margin && this.sellingPointPercent < 100 - margin);
  }

  updateLifecycleDetails(): void {
    if (!this.lifecycle?.retirePointKm) {
      this.lifecycleFillPercent = 0;
      this.sellingPointPercent = 0;

      return;
    }

    const odometerKm = this.lifecycle.odometerKm ?? 0;
    const sellingPointKm = this.lifecycle.sellingPointKm ?? 0;
    const retirePointKm = this.lifecycle.retirePointKm;

    const normalizeRatio = (value: number) => Math.max(0, Math.min(1, value));
    this.lifecycleFillPercent = normalizeRatio(odometerKm / retirePointKm) * 100;
    this.sellingPointPercent = normalizeRatio(sellingPointKm / retirePointKm) * 100;
  }
}